import { Badge } from '@amore/ui/components/badge'
import { Button } from '@amore/ui/components/button'
import { Field } from '@amore/ui/components/field'
import { Input } from '@amore/ui/components/input'
import { ClipboardList, Pencil } from 'lucide-react'
import { useState } from 'react'
import { formatMoney } from '@/lib/format'
import { dietLabels, supplierStatusLabels, supplierStatusTone } from '@/lib/labels'
import type { Diet, SupplierStatus } from '@/mock/types'
import { useToast } from '@/shell/toaster'
import { DataTable } from './data-table'
import { DetailSheet, FormDialog } from './dialog'
import { EmptyState } from './empty-state'
import { FilterBar } from './filter-bar'
import { CheckboxGrid, ItemAddRow, ItemCard, ItemCardFields } from './item-card'
import { LinkedRecord } from './linked-record'
import { Screen, ScreenSection } from './screen'
import { StatSummary } from './stat-summary'

type Row = {
  id: string
  name: string
  category: string
  status: SupplierStatus
  amount: number
  paid: number
}
const rows: Row[] = [
  {
    id: '1',
    name: 'Pronájem zámku',
    category: 'Zámek Loučeň',
    status: 'booked',
    amount: 160_000,
    paid: 40_000,
  },
  {
    id: '2',
    name: 'Fotograf celý den',
    category: 'Foto Král',
    status: 'booked',
    amount: 38_000,
    paid: 8_000,
  },
  {
    id: '3',
    name: 'Svatební menu',
    category: 'Gastro Lipová',
    status: 'agreed',
    amount: 96_000,
    paid: 0,
  },
  {
    id: '4',
    name: 'Svatební dort',
    category: 'Cukrárna U Zlatého kosa',
    status: 'cancelled',
    amount: 0,
    paid: 0,
  },
]
const dietOptions = (Object.keys(dietLabels) as Diet[]).map((value) => ({
  value,
  label: dietLabels[value],
}))

/** /patterns: every shared pattern with sample content. See PATTERNS.md. */
export function PatternsDemo() {
  const { showToast } = useToast()
  const [filter, setFilter] = useState<'all' | 'open' | 'done'>('all')
  const [search, setSearch] = useState('')
  const [dialog, setDialog] = useState(false)
  const [sheet, setSheet] = useState(false)
  const [name, setName] = useState('')
  const [diets, setDiets] = useState<Diet[]>(['vegetarian'])

  return (
    <Screen
      eyebrow="Prototyp"
      title="Vzory"
      description="Společné prvky, ze kterých se skládají všechny obrazovky. Pravidla jsou v PATTERNS.md."
    >
      <ScreenSection
        title="StatSummary"
        description="Jedna karta s přehledem čísel pro každou sekci."
      >
        <StatSummary
          headline={{ label: 'Pozvaní hosté', value: 92, caption: 've 34 pozvánkách' }}
          groups={[
            {
              title: 'Odpovědi hostů',
              value: '76 %',
              progress: { value: 70, max: 92, label: 'Odpovědělo 70 z 92' },
              tiles: [
                {
                  label: 'Odpovědělo',
                  value: 70,
                  caption: 'Potvrdili nebo odmítli účast',
                  onSelect: () => {},
                },
                {
                  label: 'Bez odpovědi',
                  value: 22,
                  caption: 'Pozvaní i čekající',
                  onSelect: () => {},
                },
              ],
            },
            {
              title: 'Účast hostů',
              tiles: [
                { label: 'Přijdou', value: 64, caption: 'Potvrdili účast', tone: 'success' },
                { label: 'Nepřijdou', value: 6, caption: 'Odmítli pozvání' },
              ],
            },
          ]}
        />
      </ScreenSection>

      <ScreenSection
        title="FilterBar a DataTable"
        description="Jeden způsob filtrování, tabulky se zarovnanými sloupci."
      >
        <FilterBar
          filters={[
            { value: 'all', label: 'Vše', count: 4 },
            { value: 'open', label: 'K zaplacení', count: 3 },
            { value: 'done', label: 'Zaplacené', count: 0 },
          ]}
          value={filter}
          onChange={setFilter}
          search={{ value: search, onChange: setSearch, placeholder: 'Hledat položku' }}
        />
        <DataTable
          aria-label="Ukázková tabulka"
          getRowId={(row) => row.id}
          onRowClick={() => setSheet(true)}
          rowMuted={(row) => row.status === 'cancelled'}
          rowActions={() => (
            <Button
              appearance="ghost"
              tone="neutral"
              size="icon"
              aria-label="Upravit"
              className="size-9"
            >
              <Pencil className="size-4" aria-hidden="true" />
            </Button>
          )}
          columns={[
            {
              key: 'name',
              header: 'Položka',
              width: 'minmax(0,1fr)',
              primary: true,
              cell: (row) => row.name,
            },
            { key: 'category', header: 'Dodavatel', width: '14rem', cell: (row) => row.category },
            {
              key: 'status',
              header: 'Stav',
              width: '11rem',
              cell: (row) => (
                <Badge tone={supplierStatusTone(row.status)}>
                  {supplierStatusLabels[row.status]}
                </Badge>
              ),
            },
            {
              key: 'paid',
              header: 'Zaplaceno',
              width: '8rem',
              align: 'end',
              numeric: true,
              cell: (row) => formatMoney(row.paid),
            },
            {
              key: 'amount',
              header: 'Cena',
              width: '8rem',
              align: 'end',
              numeric: true,
              cell: (row) => (row.amount ? formatMoney(row.amount) : '—'),
            },
          ]}
          groups={[
            {
              id: 'venue',
              label: 'Místo a hostina',
              meta: '2 položky',
              rows: rows.filter((row) => row.id === '1' || row.id === '3'),
              summary: { amount: formatMoney(256_000) },
            },
            {
              id: 'other',
              label: 'Ostatní',
              meta: '2 položky',
              rows: rows.filter((row) => row.id === '2' || row.id === '4'),
              summary: { amount: formatMoney(38_000) },
            },
          ]}
        />
      </ScreenSection>

      <ScreenSection title="LinkedRecord" description="Odkaz na záznam, který patří jinému modulu.">
        <LinkedRecord
          module="budget"
          title="Foto Král"
          meta="Záloha · 8 000 Kč · splatnost 16. 10. 2026"
          actionLabel="Otevřít v rozpočtu"
        />
        <LinkedRecord
          module="suppliers"
          title="Mgr. Eva Dvořáková"
          meta="Oddávající"
          status={<Badge tone="accent">Domluveno</Badge>}
          actionLabel="Otevřít dodavatele"
        />
      </ScreenSection>

      <ScreenSection
        title="ItemCard"
        description="Opakovaná položka ve formuláři: osoba, platba, část dne."
      >
        <div className="space-y-3">
          <ItemCard
            index={1}
            title="Jana Nováková"
            fallbackTitle="Host"
            meta="Potvrzeno"
            menu={[{ label: 'Odebrat osobu', onSelect: () => {}, tone: 'danger' }]}
          >
            <ItemCardFields>
              <Field label="Jméno">
                <Input defaultValue="Jana" />
              </Field>
              <Field label="Příjmení">
                <Input defaultValue="Nováková" />
              </Field>
              <CheckboxGrid
                label="Dietní požadavky"
                options={dietOptions}
                value={diets}
                onChange={setDiets}
              />
            </ItemCardFields>
          </ItemCard>
          <ItemCard
            index={2}
            title=""
            fallbackTitle="Doprovod"
            menu={[{ label: 'Odebrat osobu', onSelect: () => {}, tone: 'danger' }]}
          >
            <ItemCardFields>
              <Field label="Jméno doprovodu (volitelné)">
                <Input />
              </Field>
              <Field label="Příjmení">
                <Input />
              </Field>
            </ItemCardFields>
          </ItemCard>
          <ItemAddRow>
            <Button appearance="outline" tone="neutral" size="sm">
              + Přidat osobu
            </Button>
            <Button appearance="outline" tone="neutral" size="sm">
              + Přidat doprovod
            </Button>
          </ItemAddRow>
        </div>
      </ScreenSection>

      <ScreenSection title="FormDialog a DetailSheet" description="Zavírá se jen křížkem v rohu.">
        <div className="flex gap-3">
          <Button appearance="outline" tone="neutral" onClick={() => setDialog(true)}>
            Otevřít dialog
          </Button>
          <Button appearance="outline" tone="neutral" onClick={() => setSheet(true)}>
            Otevřít detail
          </Button>
        </div>
        <FormDialog
          open={dialog}
          onOpenChange={(open) => {
            setDialog(open)
            if (!open) setName('')
          }}
          title="Přidat úkol"
          description="Zapište, co potřebujete zařídit. Podrobnosti můžete doplnit později."
          dirty={name.length > 0}
          primaryAction={{
            label: 'Přidat úkol',
            onClick: () => setDialog(false),
            disabled: !name.trim(),
          }}
        >
          <Field label="Název">
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
        </FormDialog>
        <DetailSheet
          open={sheet}
          onOpenChange={setSheet}
          eyebrow="Dodavatel"
          title="Zámek Loučeň"
          description="Místo · Podepsáno"
          primaryAction={{ label: 'Uložit', onClick: () => setSheet(false) }}
          destructiveAction={{ label: 'Odebrat dodavatele', onClick: () => setSheet(false) }}
        >
          <LinkedRecord
            module="budget"
            title="Zámek Loučeň"
            meta="160 000 Kč · zaplaceno 0 Kč"
            actionLabel="Otevřít v rozpočtu"
          />
        </DetailSheet>
      </ScreenSection>

      <ScreenSection title="EmptyState" description="Jen akce, které v tu chvíli dávají smysl.">
        <EmptyState
          icon={<ClipboardList />}
          title="Zatím tu nejsou žádné úkoly"
          description="Přidejte první úkol a začněte mít svatební přípravy pod kontrolou."
          actions={<Button>Přidat první úkol</Button>}
        />
      </ScreenSection>

      <ScreenSection
        title="Oznámení"
        description="Vpravo dole. Nanejvýš dvě malá tlačítka vpravo dole: outline a solid."
      >
        <div className="flex flex-wrap gap-2">
          <Button
            appearance="outline"
            tone="neutral"
            size="sm"
            onClick={() => showToast({ title: 'Úkol uložen', tone: 'success' })}
          >
            Jen titulek
          </Button>
          <Button
            appearance="outline"
            tone="neutral"
            size="sm"
            onClick={() =>
              showToast({
                title: 'Úkol dokončen',
                description: 'Zarezervovat fotografa',
                tone: 'success',
                action: { label: 'Vrátit zpět', altText: 'Vrátit úkol', onClick: () => {} },
              })
            }
          >
            Se zpět
          </Button>
          <Button
            appearance="outline"
            tone="neutral"
            size="sm"
            onClick={() =>
              showToast({
                title: 'Dodavatel přidán',
                description: 'Zámek Loučeň',
                tone: 'success',
                action: { label: 'Vrátit zpět', altText: 'Odebrat dodavatele', onClick: () => {} },
                primaryAction: {
                  label: 'Přidat platbu',
                  altText: 'Přidat platbu do rozpočtu',
                  onClick: () => {},
                },
              })
            }
          >
            Se dvěma akcemi
          </Button>
          <Button
            appearance="outline"
            tone="neutral"
            size="sm"
            onClick={() =>
              showToast({
                title: 'Uložení se nepovedlo',
                description: 'Zkontrolujte připojení a zkuste to znovu.',
                tone: 'danger',
                primaryAction: {
                  label: 'Zkusit znovu',
                  altText: 'Zkusit uložit znovu',
                  onClick: () => {},
                },
              })
            }
          >
            Chyba
          </Button>
        </div>
      </ScreenSection>
    </Screen>
  )
}
